import UserPin from '../leaflet/_UserPin';
import TargetPin from '../leaflet/_TargetPin';
import SiteMapPin from '../leaflet/_SiteMapPin';
import ExteriorMapPin from '../leaflet/_ExteriorMapPin';
import FoodPin from '../leaflet/_FoodPin';
import AttentionPin from '../leaflet/_AttentionPin';
import ConstructionPin from '../leaflet/_ConstructionPin';
import NumSymbol from '../leaflet/_NumSymbol';
import TextMarker from '../leaflet/_TextMarker';
import MapSymbolGroup from '../leaflet/_MapSymbolGroup';
import MapDataService from '../services/MapDataService';
import LocaleService from '../services/LocaleService';
import ConstructionConfig from '../_ConstructionConfig';
import { LANGS } from './MapConstants';
import MAP_DATA from '../../_json/_map_data.json';

class MapPinManager
{
    constructor(map, rc, lang, onPinClick)
    {
        this.map = map;
        this.rc = rc;
        this.lang = lang;
        this.onPinClick = onPinClick;
        
        // Pin references
        this.userPinRef = null;
        this.targetPinRef = null;
        this.sitePinsRef = [];
        this.exteriorMapPinsRef = [];
        this.foodPinsRef = [];
        this.mapLabelsRef = [];
    }

    createMapPanes()
    {
        this.map.createPane('user');
        this.map.createPane('symbols');

        this.map.getPane('user').style.zIndex = 1000;
        this.map.getPane('symbols').style.zIndex = 450;
        this.map.getPane('symbols').style.pointerEvents = 'none';
    }

    initializePins()
    {
        // Reset pin arrays
        this.sitePinsRef = [];
        this.exteriorMapPinsRef = [];
        this.mapLabelsRef = [];

        // Create user and target pins
        const userPin = new UserPin([0, 0]);
        userPin.addTo(this.map);

        const targetPin = new TargetPin([0, 0]);
        targetPin.addTo(this.map);

        this.userPinRef = userPin;
        this.targetPinRef = targetPin;
    }

    async addBuildingPins()
    {
        const pinData = await MapDataService.getPinData('buildings', this.rc, this.lang, 'name', ['zone', 'zone_site_id']);
        
        pinData.forEach(item =>
        {
            const zone = item.zone;
            const zone_id = 'wce'.indexOf(item.zone) + 1;
            const zone_site_id = item.zone_site_id;

            const img = `${process.env.PUBLIC_URL}/img/tatemono/tatemono_thumb_${zone}_${('00' + zone_site_id).slice(-2)}.jpg`;

            const options =
            {
                zone: zone,
                zoneID: zone_id,
                zoneSiteID: zone_site_id,
                img: img,
                tooltipDirection: item.tt_direction,
                tooltipMinZoom: item.tt_min_zoom,
                isConstruction: ConstructionConfig.isConstruction(item.id),
            };

            const mapPin = new SiteMapPin(item.id, item.latlng, item.texts, this.lang, options);

            mapPin.onClick = () => 
            {
                this.onPinClick('site', item.id);
            };

            mapPin.addTo(this.map);
            this.sitePinsRef.push(mapPin);
        });
    }

    async addExteriorPins()
    {
        const pinData = await MapDataService.getPinData('exteriors', this.rc, this.lang, 'name', []);
        
        pinData.forEach(item =>
        {
            const options =
            {
                tooltipDirection: item.tt_direction,
                tooltipMinZoom: item.tt_min_zoom,
                monumentID: item.id
            };

            const mapPin = new ExteriorMapPin(item.id, item.latlng, item.texts, this.lang, options);
            
            mapPin.onClick = () => 
            {
                this.onPinClick('exterior', item.id);
            };

            mapPin.addTo(this.map);
            this.exteriorMapPinsRef.push(mapPin);
        });
    }

    addFoodPins()
    {
        MapDataService.getPinData2(MAP_DATA.restaurants, this.rc, false, ['pin_type']).forEach(item =>
        {
            const img = `${process.env.PUBLIC_URL}/img/restaurant/restaurant_pic_${('00' + item.id).slice(-2)}_1.jpg`;

            const options =
            {
                img: img
            };

            const mapPin = new FoodPin(item.id, item.latlng, item.pin_type, options);

            mapPin.onClick = () => 
            {
                this.onPinClick('food', item.id);
            };

            mapPin.addTo(this.map);
            this.foodPinsRef.push(mapPin);
        });
    }

    addSymbols()
    {
        let parsedSymbols = MapDataService.parseSymbolsData(MAP_DATA.facilities, this.rc);

        parsedSymbols.forEach(item =>
        {
            const mapSymbolGroup = new MapSymbolGroup(item.latlng, item);
            mapSymbolGroup.addTo(this.map);
        });
    }

    addInclines()
    {
        MAP_DATA.inclines.forEach(item =>
        {
            item.className = 'incline';
            item.latlng = MapDataService.getCoords(item.x, item.y, this.rc);
            item.img = `${process.env.PUBLIC_URL}/img/icon_incline.png`;

            const numSymbol = new NumSymbol(item.latlng, item.value + '°', item);
            numSymbol.addTo(this.map);
        });
    }

    addAttentionPins()
    {
        MapDataService.getPinData2(MAP_DATA.cautions, this.rc).forEach(item =>
        {
            const mapPin = new AttentionPin(item.latlng);
            
            mapPin.onClick = () => 
            {
                this.onPinClick('attention', item.key);
            };

            mapPin.addTo(this.map);
        });
    }

    addLabels()
    {
        MAP_DATA['map-labels'].filter(item => item.key.startsWith('pt')).forEach(item =>
        {
            const coords = MapDataService.getCoords(item.x, item.y, this.rc);

            const texts = {};
            LANGS.forEach((langObj) => 
            {
                // Get localized text from locale system using the label key
                const localizedText = LocaleService.getItemData(langObj.code, 'map-labels', item.key);
                // Use the localized text if available, otherwise fall back to the key
                texts[langObj.code] = localizedText || item.key;
            });

            const options =
            {
                minZoomLevel: item.min_zoom
            };

            const textMarker = new TextMarker(coords, texts, this.lang, item.fontsize, item.rotation, options);
            textMarker.addTo(this.map);

            this.mapLabelsRef.push(textMarker);
        });
    }

    async addAllMapFeatures()
    {
        this.createMapPanes();
        this.initializePins();
        await this.addBuildingPins();
        await this.addExteriorPins();
        this.addFoodPins();
        this.addSymbols();
        this.addInclines();
        this.addAttentionPins();
        this.addLabels();
    }

    updateLanguage(newLang)
    {
        this.lang = newLang;

        // Update language for SiteMapPins
        this.sitePinsRef.forEach(pin =>
        {
            if (pin._map && pin._map.getContainer())
            {
                pin.lang = newLang;
                // Force tooltip update
                const tooltip = pin.getTooltip();
                if (tooltip) 
                {
                    tooltip.update();
                }
            }
        });

        // Update language for ExteriorMapPins
        this.exteriorMapPinsRef.forEach(pin =>
        {
            if (pin._map && pin._map.getContainer())
            {
                pin.lang = newLang;
                // Force tooltip update
                const tooltip = pin.getTooltip();
                if (tooltip) 
                {
                    tooltip.update();
                }
            }
        });

        // Update language for TextMarkers (labels)
        this.mapLabelsRef.forEach(label =>
        {
            if (label._map && label._map.getContainer())
            {
                label.lang = newLang;
            }
        });
    }

    // Getters for pin references
    getUserPinRef() { return this.userPinRef; }
    getTargetPinRef() { return this.targetPinRef; }
    getSitePinsRef() { return { current: this.sitePinsRef }; }
    getExteriorMapPinsRef() { return { current: this.exteriorMapPinsRef }; }
    getFoodPinsRef() { return { current: this.foodPinsRef }; }
    getMapLabelsRef() { return { current: this.mapLabelsRef }; }
}

export default MapPinManager; 